import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, onSnapshot, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { Alert, AlertInput } from '../types/alerts';

export const useAlerts = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, userData } = useAuth();
  const { selectedClient } = useClient();

  // Cargar alertas en tiempo real
  useEffect(() => {
    let isMounted = true;

    if (!selectedClient) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const alertsRef = collection(db, 'alerts');

    // Query todas las alertas del cliente
    const alertsQuery = query(
      alertsRef,
      where('clientId', '==', selectedClient.clientId)
    );

    const unsubscribe = onSnapshot(alertsQuery, (snapshot) => {
      if (!isMounted) return;

      const alertsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Alert[];

      // Ordenar en cliente para evitar requerir un índice compuesto
      alertsData.sort((a, b) => {
        const aTime = (a.createdAt as Timestamp | undefined)?.toMillis?.() || 0;
        const bTime = (b.createdAt as Timestamp | undefined)?.toMillis?.() || 0;
        return bTime - aTime;
      });

      // Filtrar: mostrar públicas + privadas del usuario actual
      const filteredAlerts = alertsData.filter(alert =>
        alert.isPublic || alert.createdBy === currentUser?.uid
      );

      setAlerts(filteredAlerts);
      setLoading(false);
    }, (err) => {
      if (!isMounted) return;

      // Ignorar errores internos de Firestore que no afectan la funcionalidad
      if (err.message?.includes('INTERNAL ASSERTION FAILED')) {
        console.warn('Firestore internal warning (ignorable):', err.message);
        return;
      }

      console.error('Error loading alerts:', err);
      setError('Error al cargar las alertas');
      setLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [currentUser, selectedClient]);

  // Crear nueva alerta
  const cleanObject = <T extends Record<string, any>>(obj: T): T => {
    if (!obj || typeof obj !== 'object') return obj;
    return Object.fromEntries(
      Object.entries(obj).filter(([, value]) => value !== undefined)
    ) as T;
  };

  const createAlert = useCallback(async (alertData: AlertInput): Promise<string> => {
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    if (!userData) {
      throw new Error('Datos de usuario no disponibles');
    }

    if (!selectedClient) {
      throw new Error('No hay cliente seleccionado');
    }

    // Validar conflictos: misma asignación (producto/sku/grupo) y mismo tipo
    const hasConflict = alerts.some(existing => {
      if (existing.alertType !== alertData.alertType) return false;
      if (existing.assignmentType !== alertData.assignmentType) return false;

      switch (alertData.assignmentType) {
        case 'individual':
          if (!existing.productIds || !alertData.productIds) return false;
          return existing.productIds.some(id => alertData.productIds!.includes(id));
        case 'sku':
          return existing.skuCode && alertData.skuCode && existing.skuCode === alertData.skuCode;
        case 'comparison_group':
          return existing.comparisonGroupId && alertData.comparisonGroupId && existing.comparisonGroupId === alertData.comparisonGroupId;
        default:
          return false;
      }
    });

    if (hasConflict) {
      throw new Error('Ya existe una alerta con el mismo tipo y alcance; ajusta o elimina la anterior para evitar conflictos.');
    }

    const cleanedTrigger = cleanObject(alertData.triggerConfig || {});
    const cleanedEmail = cleanObject(alertData.emailNotification || {});

    const newAlert: Omit<Alert, 'id'> = cleanObject({
      ...alertData,
      triggerConfig: cleanedTrigger,
      emailNotification: cleanedEmail,
      clientId: selectedClient.clientId,
      createdBy: currentUser.uid,
      createdByName: userData.name || currentUser.email || 'Usuario',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    try {
      const docRef = await addDoc(collection(db, 'alerts'), newAlert);
      return docRef.id;
    } catch (err) {
      console.error('Error creating alert:', err);
      throw new Error(`Error al crear la alerta: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userData, selectedClient]);

  // Actualizar alerta existente
  const updateAlert = useCallback(async (
    alertId: string,
    updates: Partial<AlertInput>
  ): Promise<void> => {
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      throw new Error('Alerta no encontrada');
    }

    // Solo el creador puede editar
    if (alert.createdBy !== currentUser.uid) {
      throw new Error('No tienes permisos para editar esta alerta');
    }

    try {
      await updateDoc(doc(db, 'alerts', alertId), {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error updating alert:', err);
      throw new Error(`Error al actualizar la alerta: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
  }, [currentUser, alerts]);

  // Eliminar alerta
  const deleteAlert = useCallback(async (alertId: string): Promise<void> => {
    if (!currentUser) {
      throw new Error('Usuario no autenticado');
    }

    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      throw new Error('Alerta no encontrada');
    }

    // Solo el creador puede eliminar
    if (alert.createdBy !== currentUser.uid) {
      throw new Error('No tienes permisos para eliminar esta alerta');
    }

    try {
      await deleteDoc(doc(db, 'alerts', alertId));
    } catch (err) {
      console.error('Error deleting alert:', err);
      throw new Error(`Error al eliminar la alerta: ${err instanceof Error ? err.message : 'Error desconocido'}`);
    }
  }, [currentUser, alerts]);

  // Toggle estado activo/inactivo
  const toggleAlertActive = useCallback(async (alertId: string): Promise<void> => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      throw new Error('Alerta no encontrada');
    }

    await updateAlert(alertId, { isActive: !alert.isActive });
  }, [alerts, updateAlert]);

  // Obtener alertas para un producto específico
  const getAlertsForProduct = useCallback((productId: string): Alert[] => {
    return alerts.filter(alert =>
      alert.assignmentType === 'individual' &&
      alert.productIds?.includes(productId)
    );
  }, [alerts]);

  // Obtener alertas para un SKU específico
  const getAlertsForSku = useCallback((skuCode: string): Alert[] => {
    return alerts.filter(alert =>
      alert.assignmentType === 'sku' &&
      alert.skuCode === skuCode
    );
  }, [alerts]);

  // Agregar productos a una alerta existente
  const addProductsToAlert = useCallback(async (
    alertId: string,
    productIds: string[]
  ): Promise<void> => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      throw new Error('Alerta no encontrada');
    }

    if (alert.assignmentType !== 'individual') {
      throw new Error('Solo se pueden agregar productos a alertas individuales');
    }

    const existingIds = alert.productIds || [];
    const newIds = Array.from(new Set([...existingIds, ...productIds]));

    await updateAlert(alertId, { productIds: newIds });
  }, [alerts, updateAlert]);

  // Remover productos de una alerta
  const removeProductsFromAlert = useCallback(async (
    alertId: string,
    productIds: string[]
  ): Promise<void> => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) {
      throw new Error('Alerta no encontrada');
    }

    if (alert.assignmentType !== 'individual') {
      throw new Error('Solo se pueden remover productos de alertas individuales');
    }

    const existingIds = alert.productIds || [];
    const newIds = existingIds.filter(id => !productIds.includes(id));

    await updateAlert(alertId, { productIds: newIds });
  }, [alerts, updateAlert]);

  // Crear alertas masivas para múltiples productos
  const createBulkAlerts = useCallback(async (
    alertData: AlertInput,
    productIds: string[]
  ): Promise<string> => {
    // Para alertas individuales, crear una sola alerta con todos los productos
    const input: AlertInput = {
      ...alertData,
      assignmentType: 'individual',
      productIds
    };
    return createAlert(input);
  }, [createAlert]);

  // Obtener conteo de alertas activas por producto
  const getAlertCountForProduct = useCallback((productId: string): number => {
    return getAlertsForProduct(productId).filter(a => a.isActive).length;
  }, [getAlertsForProduct]);

  // Verificar si un producto tiene alertas disparadas recientemente
  // Esto requiere consultar alertTriggered, pero por ahora retornamos false
  // La lógica real se implementará en useAlertTriggered
  const hasRecentlyTriggered = useCallback((_productId: string): boolean => {
    return false; // TODO: Implementar con useAlertTriggered
  }, []);

  return {
    alerts,
    loading,
    error,
    createAlert,
    updateAlert,
    deleteAlert,
    toggleAlertActive,
    getAlertsForProduct,
    getAlertsForSku,
    addProductsToAlert,
    removeProductsFromAlert,
    createBulkAlerts,
    getAlertCountForProduct,
    hasRecentlyTriggered
  };
};
